import type { Metric } from 'web-vitals'

/**
 * RUM-мониторинг Core Web Vitals.
 *
 * Метрики (LCP, CLS, INP, FCP, TTFB) отправляются в Яндекс.Метрику
 * параметрами визита: Отчёты → Содержание → Параметры визитов → webVitals.
 *
 * Значения округляются и бакетируются по порогам Google
 * (good / needs-improvement / poor), чтобы в Метрике можно было
 * сегментировать визиты без пост-обработки.
 */
export default defineNuxtPlugin(() => {
    const {
        public: { yandexMetrikaId },
    } = useRuntimeConfig()

    if (!yandexMetrikaId) {
        return
    }

    const report = (metric: Metric) => {
        const ym = (window as any).ym
        if (typeof ym !== 'function') {
            return
        }

        // Метрики приходят отложенно (INP/CLS — вплоть до ухода со страницы), поэтому проверка
        // именно в момент отправки: SPA-переход мог увести на URL с токеном доступа.
        if (isPrivateAnalyticsHere()) {
            return
        }

        ym(yandexMetrikaId, 'params', {
            webVitals: {
                [metric.name]: {
                    // CLS — безразмерный, остальные в мс
                    value: metric.name === 'CLS'
                        ? Math.round(metric.value * 1000) / 1000
                        : Math.round(metric.value),
                    rating: metric.rating,
                    navigationType: metric.navigationType,
                },
            },
        })
    }

    const registerObservers = async () => {
        // web-vitals 5 uses Array.prototype.at internally; older browsers can still use the site.
        if (typeof Array.prototype.at !== 'function') {
            return
        }

        const {onLCP, onCLS, onINP, onFCP, onTTFB} = await import('web-vitals')

        onLCP(report)
        onCLS(report)
        onINP(report)
        onFCP(report)
        onTTFB(report)
    }

    const scheduleRegistration = () => {
        const requestIdle = window.requestIdleCallback
        if (typeof requestIdle === 'function') {
            requestIdle(() => void registerObservers(), {timeout: 2000})
            return
        }

        setTimeout(() => void registerObservers(), 0)
    }

    scheduleRegistration()
})
